// Doctor-editable rules that pre-load an entire lab package (or specific
// tests) on every new visit when the patient's known_case_of matches a trigger.
// This means a diabetic walk-in gets Diabetes Screening tests auto-populated
// before the doctor touches the keyboard.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, RotateCcw, Trash2, TestTube2, AlertCircle,
} from "lucide-react";
import { CLINIC, LogoTile } from "../lib/clinic";

// Kept in sync with the LAB_PACKAGES list in PatientDetail.jsx — if you add
// packages there, add them here too so the picker stays complete.
const PACKAGE_OPTIONS = [
  { key: "DIAB",    label: "Diabetes Screening" },
  { key: "RFT",     label: "Renal Function" },
  { key: "LFT",     label: "Liver Function" },
  { key: "Lipid",   label: "Lipid" },
  { key: "CBC",     label: "Hemogram (CBC)" },
  { key: "URINE",   label: "Urine Routine" },
  { key: "ARTHR",   label: "Arthritis Profile" },
  { key: "CARDIAC", label: "Cardiac Profile" },
  { key: "IRON",    label: "Iron Studies" },
  { key: "THYROID", label: "Thyroid" },
];

const emptyRule = () => ({ trigger: "", match_any: [], packages: [], tests: [], active: true });

// Tag-editor input: chips + free-text add. Accepts comma or Enter.
function TagInput({ value, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const parts = draft.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    if (parts.length) onChange([...(value || []), ...parts.filter((p) => !value.includes(p))]);
    setDraft("");
  };
  return (
    <div className="flex flex-wrap gap-1 border border-slate-300 rounded-md p-1.5 bg-white min-h-8" data-testid={testid}>
      {(value || []).map((v, i) => (
        <span key={`${v}-${i}`} className="inline-flex items-center gap-1 bg-pink-50 text-pink-800 border border-pink-200 rounded px-1.5 py-0.5 text-[11px]">
          {v}
          <button
            onClick={() => onChange(value.filter((_, idx) => idx !== i))}
            className="hover:text-red-600"
            title="Remove"
            data-testid={`${testid}-remove-${i}`}
          >×</button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === ",") && (e.preventDefault(), commit())}
        onBlur={commit}
        placeholder={placeholder}
        className="flex-1 min-w-[100px] text-[12px] outline-none bg-transparent"
      />
    </div>
  );
}

function PackageMultiSelect({ value, onChange, testid }) {
  const toggle = (k) => {
    if (value?.includes(k)) onChange(value.filter((x) => x !== k));
    else onChange([...(value || []), k]);
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {PACKAGE_OPTIONS.map((p) => (
        <button
          key={p.key}
          onClick={() => toggle(p.key)}
          className={`text-[11px] px-2 py-0.5 rounded border ${value?.includes(p.key) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"}`}
          data-testid={`${testid}-${p.key}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export default function LabPackageSettings() {
  const [items, setItems] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/lab-preferences");
      setItems(r.data.rules || []);
      setDefaults(r.data.defaults || []);
    } catch (e) {
      toast.error("Failed to load lab preferences");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => setItems(items.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, emptyRule()]);
  const resetToDefaults = () => {
    if (window.confirm("Replace the list with the built-in defaults? Any custom entries will be lost.")) {
      setItems(defaults.map((d) => ({ ...d })));
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/lab-preferences", { rules: items });
      setItems(r.data.rules || []);
      toast.success("Lab preferences saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="p-1.5 rounded hover:bg-slate-100" data-testid="back-to-dashboard"><ArrowLeft size={16} /></Link>
          <LogoTile />
          <div>
            <div className="text-sm font-heading font-semibold text-slate-900">Lab Package Preferences</div>
            <div className="text-[11px] text-slate-500">{CLINIC.name} · {CLINIC.doctorName}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={resetToDefaults} className="gap-1" data-testid="reset-lab-prefs"><RotateCcw size={12} /> Reset to defaults</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white gap-1" data-testid="save-lab-prefs">
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md p-3 text-emerald-900 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>How this is used:</strong> When a new visit opens, we look at the patient's <em>Known Case Of</em> tags. Every active rule whose <strong>Match any</strong> list overlaps with those tags will auto-add its <strong>Packages</strong> and individual <strong>Tests</strong> to the Investigations table — before the doctor types anything. Staff can still delete rows they don't need.
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="p-3 min-w-[120px]">Rule name</th>
                <th className="p-3 min-w-[260px]">Match if Known-Case-Of contains any of</th>
                <th className="p-3 min-w-[280px]">Pre-load packages</th>
                <th className="p-3 min-w-[200px]">Extra individual tests</th>
                <th className="p-3 w-20">Active</th>
                <th className="p-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">No rules yet — tap <strong>Add rule</strong> or <strong>Reset to defaults</strong>.</td></tr>
              )}
              {items.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 align-top" data-testid={`lab-pref-row-${i}`}>
                  <td className="p-2">
                    <Input
                      value={r.trigger || ""}
                      onChange={(e) => upd(i, "trigger", e.target.value)}
                      placeholder="e.g. diabetes"
                      className="h-8 text-sm"
                      data-testid={`lab-pref-trigger-${i}`}
                    />
                  </td>
                  <td className="p-2">
                    <TagInput
                      value={r.match_any || []}
                      onChange={(v) => upd(i, "match_any", v)}
                      placeholder="Type + Enter (e.g. Type 2 DM)"
                      testid={`lab-pref-match-${i}`}
                    />
                  </td>
                  <td className="p-2">
                    <PackageMultiSelect
                      value={r.packages || []}
                      onChange={(v) => upd(i, "packages", v)}
                      testid={`lab-pref-pkg-${i}`}
                    />
                  </td>
                  <td className="p-2">
                    <TagInput
                      value={r.tests || []}
                      onChange={(v) => upd(i, "tests", v)}
                      placeholder="Type + Enter (e.g. HbA1c)"
                      testid={`lab-pref-tests-${i}`}
                    />
                  </td>
                  <td className="p-2">
                    <Switch
                      checked={r.active !== false}
                      onCheckedChange={(v) => upd(i, "active", v)}
                      data-testid={`lab-pref-active-${i}`}
                    />
                  </td>
                  <td className="p-2 text-right">
                    <button onClick={() => del(i)} className="p-1 text-red-600 hover:bg-red-50 rounded" data-testid={`lab-pref-del-${i}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-slate-200 p-3 flex justify-between items-center">
            <Button variant="outline" size="sm" onClick={add} className="gap-1" data-testid="add-lab-pref"><Plus size={12} /> Add rule</Button>
            <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
              <TestTube2 size={12} className="text-emerald-600" /> {items.length} rule{items.length === 1 ? "" : "s"} — active tags on the patient trigger these automatically
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
